import type { Config, DriveStep, Driver } from 'driver.js'
import { storeToRefs } from 'pinia'
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { useUserStore } from '@/store/user'
import { trackEvent } from '@/lib/track'

// Drapeau in-memory : WelcomeScreen demande le tour, DeckView le déclenche quand les ancres sont dans le DOM.
const autoStartPending = ref(false)
export function scheduleAutoTour(): void {
  autoStartPending.value = true
}

// Un même data-tour peut coexister desktop + mobile ; renvoie celui réellement visible.
function pickVisible(selector: string): Element {
  const els = document.querySelectorAll<HTMLElement>(selector)
  return findVisible(selector) ?? els[0]
}

function findVisible(selector: string): HTMLElement | undefined {
  return Array.from(document.querySelectorAll<HTMLElement>(selector)).find(
    el => el.offsetParent !== null
  )
}

function closeOpenDialogs() {
  document
    .querySelectorAll<HTMLDialogElement>('dialog[open]')
    .forEach(d => d.close())
}

export function useGuidedTour() {
  const { t } = useI18n({ useScope: 'global' })
  const { aiEnabled } = storeToRefs(useUserStore())
  const router = useRouter()

  function buildSteps(): DriveStep[] {
    const steps: DriveStep[] = [
      {
        popover: {
          title: t('onboarding.tour.steps.welcome.title'),
          description: t('onboarding.tour.steps.welcome.description')
        }
      },
      {
        // Plusieurs [data-tour="omnibox"] (desktop + mobile) ; on force celui visible.
        element: () => pickVisible('[data-tour="omnibox"]'),
        popover: {
          title: t('onboarding.tour.steps.omnibox.title'),
          description: t('onboarding.tour.steps.omnibox.description')
        }
      },
      {
        element: '[data-drag-handle]',
        popover: {
          title: t('onboarding.tour.steps.column.title'),
          description: t('onboarding.tour.steps.column.description')
        }
      },
      {
        element: '[data-tour="theme-toggle"]',
        popover: {
          title: t('onboarding.tour.steps.theme.title'),
          description: t('onboarding.tour.steps.theme.description')
        }
      }
    ]

    if (aiEnabled.value) {
      // Sur le deck (navbar visible), avant le step IA qui navigue vers /ai.
      steps.push({
        // Sur mobile le micro vit dans la modale de recherche, fermée : on pointe la loupe.
        element: () =>
          findVisible('[data-tour="voice-button"]') ??
          pickVisible('[data-tour="omnibox"]'),
        popover: {
          title: t('onboarding.tour.steps.voice.title'),
          description: t('onboarding.tour.steps.voice.description')
        }
      })
      steps.push({
        element: '[data-tour="ai-button"]',
        popover: {
          title: t('onboarding.tour.steps.ai.title'),
          description: t('onboarding.tour.steps.ai.description')
        },
        onHighlightStarted: () => {
          // Ouvre le panneau IA pour le voir en contexte.
          if (router.currentRoute.value.name !== 'ai') {
            void router.push({ name: 'ai' })
          }
        }
      })
    }

    steps.push({
      // Page About : QR / install / partage selon le device.
      popover: {
        title: t('onboarding.tour.steps.cross-device.title'),
        description: t('onboarding.tour.steps.cross-device.description')
      },
      onHighlightStarted: () => {
        if (router.currentRoute.value.name !== 'about') {
          void router.push({ name: 'about' })
        }
      }
    })

    steps.push({
      // Pointe l'icône de relance du tour, pour la retrouver plus tard.
      element: '[data-tour="tour-trigger"]',
      popover: {
        title: t('onboarding.tour.steps.end.title'),
        description: t('onboarding.tour.steps.end.description'),
        onNextClick: (_el, _step, { driver: driverInstance }) => {
          completedNormally = true
          closeOpenDialogs()
          void router.push({ name: 'deck' })
          driverInstance.destroy()
        }
      },
      onHighlightStarted: () => {
        if (router.currentRoute.value.name !== 'deck') {
          void router.push({ name: 'deck' })
        }
      }
    })
    return steps
  }

  // Distingue un tour terminé (bouton "Terminer") d'un tour fermé en cours (X, échap, overlay).
  let completedNormally = false

  async function start() {
    // Les ancres du tour vivent sur la vue deck : on y navigue d'abord.
    if (router.currentRoute.value.name !== 'deck') {
      await router.push({ name: 'deck' })
    }
    completedNormally = false
    const config: Config = {
      showProgress: true,
      progressText: t('onboarding.tour.progress', {
        current: '{{current}}',
        total: '{{total}}'
      }),
      nextBtnText: t('onboarding.tour.next'),
      prevBtnText: t('onboarding.tour.prev'),
      doneBtnText: t('onboarding.tour.done'),
      allowClose: true,
      steps: buildSteps(),
      onDestroyed: () => {
        trackEvent(completedNormally ? 'tour_completed' : 'tour_dismissed')
      }
    }
    const [{ driver }] = await Promise.all([
      import('driver.js'),
      import('driver.js/dist/driver.css')
    ])
    const instance: Driver = driver(config)
    instance.drive()
  }

  return { start, autoStartPending }
}
